// app/[lang]/(dashboard)/company-certificates/_components/certificates-grid.tsx

"use client";

import { ComboBoxSimpleType } from "@/components/ui/combobox-simple";
import React, { useEffect, useRef, useState, useTransition } from "react";

// Lightbox Imports
import { useIsArabic } from "@/hooks/use-isArabic";
import { Certificate } from "@/lib/types";
import { _t, cn } from "@/lib/utils";
import { X } from "lucide-react";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/plugins/captions.css";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import CertificateCard from "./certificate-card";
import CertificateFilter from "./certificate-filter";

interface CertificateGridProps {
  certificates: Certificate[];
}

function toSlug(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function CertificateGrid({ certificates }: CertificateGridProps) {
  const { isArabic, lang } = useIsArabic();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [isPending, startTransition] = useTransition();
  const [pendingFilter, setPendingFilter] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSlides, setLightboxSlides] = useState<{ src: string; title: string }[]>([]);
  const [lightboxCert, setLightboxCert] = useState<Certificate | null>(null);
  const thumbnailsRef = useRef<any>(null);
  const zoomRef = useRef<any>(null);
  const overProductStrip = useRef(false);
  const [panelOpen, setPanelOpen] = useState(true);

  const publishedCertificates = certificates.filter((c) => c.status === "publish");

  const categories = [
    "All",
    ...Array.from(
      new Set(
        publishedCertificates.flatMap((cert) =>
          cert.terms.nodes.map((term) => {
            if (isArabic && term.translations && term.translations.length > 0) {
              return term.translations[0].name;
            }
            return term.name;
          })
        )
      )
    ),
  ];

  const comboboxCategories: ComboBoxSimpleType[] = categories.map((category, index) => ({
    id: index,
    name: category,
    value: category,
  }));

  // ── URL-driven state ──────────────────────────────────────────────────────
  const filterParam = searchParams.get("filter");
  const certParam = searchParams.get("cert");

  // Map URL slug back to the actual category string
  const activeFilter =
    filterParam
      ? (categories.find((c) => toSlug(c) === filterParam) ?? "All")
      : "All";

  const filteredCertificates = publishedCertificates.filter((cert) => {
    if (activeFilter === "All") return true;
    return cert.terms.nodes.some((term) => {
      const arabicName = term.translations?.[0]?.name;
      return term.name === activeFilter || arabicName === activeFilter;
    });
  });

  // ── URL update helpers ────────────────────────────────────────────────────

  // Filter changes re-render the grid → wrap in startTransition for pending UI
  function setFilter(category: string) {
    setPendingFilter(category);
    const params = new URLSearchParams(searchParams.toString());
    if (category === "All") params.delete("filter");
    else params.set("filter", toSlug(category));
    params.delete("cert");
    const qs = params.toString();
    startTransition(() => {
      router.push(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
    });
  }

  // Clear pendingFilter once the transition settles
  useEffect(() => {
    if (!isPending) setPendingFilter(null);
  }, [isPending]);

  // Cert changes only touch the URL — no grid re-render, so skip startTransition
  // and use replace (no extra history entry for opening/closing a lightbox)
  function setCertParam(slug: string | null) {
    const params = new URLSearchParams(searchParams.toString());
    if (slug === null) params.delete("cert");
    else params.set("cert", slug);
    const qs = params.toString();
    router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
  }

  // ── Lightbox helpers ──────────────────────────────────────────────────────
  function buildSlides(certificate: Certificate) {
    const displayTitle = isArabic ? certificate.arabicTitle : certificate.title;
    const galleryImages = certificate.certificatesOption?.gallery?.nodes;
    if (galleryImages && galleryImages.length > 0) {
      return galleryImages.map((img) => ({ src: img.mediaItemUrl, title: displayTitle }));
    }
    if (certificate.featuredImage?.node?.mediaItemUrl) {
      return [{ src: certificate.featuredImage.node.mediaItemUrl, title: displayTitle }];
    }
    return [];
  }

  function openLightboxFor(certificate: Certificate) {
    const slides = buildSlides(certificate);
    if (slides.length === 0) return;
    // URL first — so the address bar shows ?cert= the instant the lightbox opens
    setCertParam(certificate.slug);
    setLightboxSlides(slides);
    setLightboxCert(certificate);
    setLightboxOpen(true);
  }

  function closeLightbox() {
    setLightboxOpen(false);
    setLightboxCert(null);
    setCertParam(null);
    setPanelOpen(false);
  }

  // ── Open lightbox on direct link (/certificates?cert=some-slug) ───────────
  const initialCertHandled = useRef(false);
  useEffect(() => {
    if (initialCertHandled.current || !certParam) return;
    initialCertHandled.current = true;
    const cert = publishedCertificates.find((c) => c.slug === certParam);
    if (!cert) return;
    const slides = buildSlides(cert);
    if (slides.length > 0) {
      setLightboxSlides(slides);
      setLightboxCert(cert);
      setLightboxOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = (imageUrl: string) => {
    window.open(imageUrl, "_blank", "noopener,noreferrer");
  };

  const countTextTemplate = isArabic
    ? "يتم عرض {{count}} من أصل {{total}} شهادة"
    : "Showing {{count}} of {{total}} certificates";
  const countText = countTextTemplate
    .replace("{{count}}", filteredCertificates.length.toString())
    .replace("{{total}}", publishedCertificates.length.toString());

  return (
    <>
      <CertificateFilter
        categories={categories}
        comboboxCategories={comboboxCategories}
        activeFilter={activeFilter}
        onFilterChangeAction={setFilter}
        toSlug={toSlug}
        pathname={pathname}
        filterParam={filterParam}
        isPending={isPending}
        pendingFilter={pendingFilter}
      />

      <div className="text-center text-gray-500 dark:text-subtitleDark mb-8">{countText}</div>

      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10 px-2 md:px-0 transition-opacity duration-200 ${isPending ? "opacity-40 pointer-events-none" : "opacity-100"}`}>
        {filteredCertificates.map((cert) => (
          <CertificateCard
            key={cert.slug}
            certificate={cert}
            onViewAction={openLightboxFor}
            onDownloadAction={handleDownload}
          />
        ))}
      </div>

      <Lightbox
        open={lightboxOpen}
        close={closeLightbox}
        slides={lightboxSlides}
        carousel={{ finite: true }}
        plugins={[Thumbnails, Captions, Zoom]}
        thumbnails={{ ref: thumbnailsRef }}
        zoom={{ ref: zoomRef }}
        on={{
          click: () => {
            if (overProductStrip.current) return;
            (thumbnailsRef.current?.visible
              ? thumbnailsRef.current?.hide
              : thumbnailsRef.current?.show)?.();
          },
        }}
        render={{
          controls: () => {
            const products = lightboxCert?.certificatesOption?.certRelatedProducts?.nodes ?? [];
            const catNodes = lightboxCert?.certificatesOption?.certProductCategories?.nodes ?? [];
            if (products.length === 0 && catNodes.length === 0) return null;

            function openLink(url: string) {
              const a = document.createElement("a");
              a.href = url;
              a.target = "_blank";
              a.rel = "noopener noreferrer";
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }

            const panelContent = (
              <div className="flex-1 overflow-y-auto p-3 space-y-4">
                {products.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-white/50 mb-2">
                      {isArabic ? "المنتجات المرتبطة" : "Products"}
                    </p>
                    <div className="space-y-1.5">
                      {products.map((product) => (
                        <button
                          key={product.slug}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openLink(product.link); }}
                          className="w-full flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/15 rounded-lg px-2.5 py-2 transition-colors text-left cursor-pointer"
                        >
                          {product.featuredImage?.node?.sourceUrl && (
                            <div className="flex items-center justify-center w-8 h-8 rounded bg-white shrink-0 overflow-hidden">
                              <Image src={product.featuredImage.node.sourceUrl} alt="" width={32} height={32} className="w-8 h-8 object-cover" unoptimized />
                            </div>
                          )}
                          <span className="text-xs text-white font-medium leading-tight line-clamp-2">
                            {product.title}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {catNodes.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-white/50 mb-2">
                      {isArabic ? "الفئات" : "Categories"}
                    </p>
                    <div className="space-y-1.5">
                      {catNodes.map((cat) => (
                        <button
                          key={cat.slug}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); cat.link && openLink(cat.link); }}
                          className="w-full flex items-center bg-primary/70 hover:bg-primary/90 rounded-lg px-3 py-2 transition-colors cursor-pointer text-left"
                        >
                          <span className="text-xs text-white font-medium leading-tight">
                            {cat.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );

            return (
              <>
                {/* Backdrop — mobile only, shown when panel open */}
                {panelOpen && (
                  <div
                    className="absolute inset-0 bg-black/40 md:hidden"
                    onClick={(e) => { e.stopPropagation(); setPanelOpen(false); }}
                  />
                )}

                {/* Pull-tab — all screen sizes, visible when panel is closed */}
                {!panelOpen && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPanelOpen(true); }}
                    className="absolute top-1/2 -translate-y-1/2 right-0 flex flex-col items-center gap-1 bg-black/70 border border-white/20 border-r-0 rounded-l-lg px-1.5 py-3 text-white/80 hover:text-white transition-colors"
                  >
                    <span className="w-3.5 h-0.5 bg-current rounded-full" />
                    <span className="w-3.5 h-0.5 bg-current rounded-full" />
                    <span className="w-3.5 h-0.5 bg-current rounded-full" />
                  </button>
                )}

                {/* Sidebar panel — unified for all screen sizes */}
                <div
                  className={cn(
                    "absolute top-0 right-0 bottom-0 w-64 md:w-56 lg:w-64 bg-black/90 md:bg-black/70 backdrop-blur-sm border-l border-white/10 flex flex-col overflow-hidden transition-transform duration-300 ease-in-out",
                    panelOpen ? "translate-x-0" : "translate-x-full"
                  )}
                  onPointerEnter={() => { overProductStrip.current = true; }}
                  onPointerLeave={() => { overProductStrip.current = false; }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between px-3 pt-3 pb-1">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40">
                      {isArabic ? "المنتجات المرتبطة" : "Associated"}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setPanelOpen(false); }}
                      className="text-white/50 hover:text-white p-1 rounded transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {panelContent}
                </div>
              </>
            );
          },
        }}
      />
    </>
  );
}
